/**
 * Portaled hover tooltip anchored to a DOM element via `parentRef`.
 *
 * Positions with `position: fixed` on `document.body`. When `wrapText` is true,
 * width is set to `maxWidth` explicitly — `maxWidth` alone does not shrink
 * fixed + width:auto content, so long messages would otherwise overflow.
 */
import React from "react";
import { createPortal } from "react-dom";

export type TooltipPlacement =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";

/**
 * Computes fixed-position CSS for a tooltip relative to an anchor rect.
 *
 * @param anchorRect - Bounding box of the hover target
 * @param placement - Preferred side/corner relative to the anchor
 * @param offsetPx - Gap between anchor and tooltip edge
 * @param maxWidth - CSS max-width applied to the positioned style
 * @returns Inline styles for the portaled tooltip container
 */
export function computeTooltipPosition(
  anchorRect: DOMRect,
  placement: TooltipPlacement,
  offsetPx: number = 15,
  maxWidth: string = "500px",
): React.CSSProperties {
  const halfWidth = anchorRect.width / 2;
  const halfHeight = anchorRect.height / 2;

  switch (placement) {
    case "right":
      return {
        position: "fixed",
        top: anchorRect.top + halfHeight,
        left: anchorRect.right + offsetPx,
        transform: "translate(0, -10px)",
        zIndex: 2147483647,
        maxWidth: maxWidth,
      };
    case "left":
      return {
        position: "fixed",
        top: anchorRect.top + halfHeight,
        left: anchorRect.left - offsetPx,
        transform: "translate(-100%, -10px)",
        zIndex: 2147483647,
        maxWidth: maxWidth,
      };
    case "top":
      return {
        position: "fixed",
        top: anchorRect.top - offsetPx,
        left: anchorRect.left + halfWidth,
        transform: "translate(-10px, -100%)",
        zIndex: 2147483647,
        maxWidth: maxWidth,
      };
    case "top-left":
      return {
        position: "fixed",
        top: anchorRect.top - offsetPx,
        left: anchorRect.left,
        transform: "translate(0, -100%)",
        zIndex: 2147483647,
        maxWidth: maxWidth,
      };
    case "top-right":
      return {
        position: "fixed",
        top: anchorRect.top - offsetPx,
        left: anchorRect.right,
        transform: "translate(-100%, -100%)",
        zIndex: 2147483647,
        maxWidth: maxWidth,
      };
    case "bottom":
    default:
      return {
        position: "fixed",
        top: anchorRect.bottom + offsetPx,
        left: anchorRect.left + halfWidth,
        transform: "translate(-10px, 0)",
        zIndex: 2147483647,
        maxWidth: maxWidth,
      };
    case "bottom-left":
      return {
        position: "fixed",
        top: anchorRect.bottom + offsetPx,
        left: anchorRect.left,
        transform: "translate(0, 0)",
        zIndex: 2147483647,
        maxWidth: maxWidth,
      };
    case "bottom-right":
      return {
        position: "fixed",
        top: anchorRect.bottom + offsetPx,
        left: anchorRect.right,
        transform: "translate(-100%, 0)",
        zIndex: 2147483647,
        maxWidth: maxWidth,
      };
  }
}

/**
 * Positions a tooltip above a row anchor, horizontally aligned with the cursor.
 *
 * @param clientX - Viewport X coordinate of the pointer
 * @param anchorTop - Viewport Y of the row's top edge (tooltip sits above this)
 * @param offsetPx - Gap between row top and tooltip bottom
 * @param maxWidth - CSS max-width for the tooltip panel
 * @returns Fixed-position styles for a portaled tooltip
 */
export function computeCursorTopTooltipPosition(
  clientX: number,
  anchorTop: number,
  offsetPx: number = 8,
  maxWidth: string = "500px",
): React.CSSProperties {
  return {
    position: "fixed",
    left: clientX,
    top: anchorTop - offsetPx,
    transform: "translate(-50%, -100%)",
    zIndex: 2147483647,
    maxWidth,
    width: "max-content",
    boxSizing: "border-box",
    whiteSpace: "nowrap",
  };
}

/**
 * Renders a hover tooltip in a portal, positioned from `parentRef`.
 *
 * @param children - Tooltip body content
 * @param title - Fallback text when `children` is absent
 * @param placement - Side/corner relative to the anchor
 * @param showTooltip - When false, renders nothing
 * @param parentRef - Anchor element used for positioning
 * @param offsetPx - Gap between anchor and tooltip (default 5)
 * @param wrapText - When true, forces width to `maxWidth` and wraps text
 * @param maxWidth - Width cap (and explicit width when wrapping); default `500px`
 * @returns Portaled tooltip node, or null when hidden / unanchored
 */
const ToolTipUsingPortal = ({
  children,
  title,
  placement,
  showTooltip,
  parentRef,
  offsetPx = 5,
  wrapText = false,
  maxWidth = "500px",
}: {
  children: React.ReactNode;
  title?: string;
  placement: TooltipPlacement;
  showTooltip: boolean;
  parentRef: React.RefObject<HTMLDivElement>;
  offsetPx?: number;
  wrapText?: boolean;
  maxWidth?: string;
}) => {
  if (!showTooltip) return null;
  const anchor = parentRef.current;
  if (!anchor) return null;

  const rect = anchor.getBoundingClientRect();
  const style: React.CSSProperties = {
    ...computeTooltipPosition(rect, placement, offsetPx, maxWidth),
    boxSizing: "border-box",
    ...(wrapText
      ? {
          // Explicit width is required: maxWidth alone does not shrink
          // position:fixed + width:auto shrink-to-fit content.
          width: maxWidth,
          maxWidth,
          whiteSpace: "normal",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        }
      : {
          width: "fit-content",
          maxWidth,
          whiteSpace: "nowrap",
        }),
  };

  return createPortal(
    <div
      style={style}
      data-placement={placement}
      role="tooltip"
      className="border-priamryGray-16 rounded-md border bg-white p-2 text-tiny shadow-lg"
    >
      {children ?? title}
    </div>,
    document.body,
  );
};

export default ToolTipUsingPortal;
